import { Injectable, OnDestroy } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { StoreService } from '@core/store.service'
import { UserService } from '@core/user.service'
import { KeycloakAuthGuard, KeycloakEventType, KeycloakService } from 'keycloak-angular'
import { CoreBasicService } from './core-basic.service'
import { Subject } from 'rxjs'

@Injectable()
export class AuthService extends KeycloakAuthGuard implements OnDestroy {
   public appRoles: any = {}

   newTokenSubject$: Subject<string> = new Subject<string>()
   licenceSubject$: Subject<boolean> = new Subject<boolean>()

   constructor(
      public store: StoreService,
      public user: UserService,
      protected router: Router,
      public keycloakAngular: KeycloakService,
      public coreBasicService: CoreBasicService
   ) {
      super(router, keycloakAngular)

      keycloakAngular.keycloakEvents$.subscribe({
         next: (e) => {
            switch (e.type) {
               case KeycloakEventType.OnAuthError:
                  this.keycloakEventLog(e.type)
                  break
               case KeycloakEventType.OnAuthLogout:
                  this.keycloakEventLog(e.type)
                  this.keycloakAngular.logout()
                  break
               case KeycloakEventType.OnAuthRefreshError:
                  this.keycloakEventLog(e.type)
                  keycloakAngular.login()
                  break
               case KeycloakEventType.OnAuthRefreshSuccess:
                  this.keycloakEventLog(e.type)
                  this.licenceSubject$.next(this.hasLicence())
                  this.getToken().then((token) => this.newTokenSubject$.next(token))
                  break
               case KeycloakEventType.OnAuthSuccess:
                  this.keycloakEventLog(e.type)
                  break
               case KeycloakEventType.OnReady:
                  this.keycloakEventLog(e.type)
                  break
               case KeycloakEventType.OnTokenExpired:
                  this.keycloakEventLog(e.type)
                  keycloakAngular.updateToken(5)
                  break
               default:
                  break
            }
         },
      })
   }

   ngOnDestroy(): void {
      this.keycloakAngular.keycloakEvents$.unsubscribe()
   }

   //-- User --//

   getAppRoles() {
      return this.appRoles
   }

   setAppRole(role: string, permissions: string[] = []) {
      role = role.trim()
      if (role) {
         this.appRoles[role] = permissions
         return true
      }
      return false
   }

   isValidAppRole(role: string) {
      return role && this.appRoles.hasOwnProperty(role)
   }

   addRoleToUser(role: string) {
      role = role.trim()
      if (role && this.isValidAppRole(role) && !this.user.hasRole(role)) {
         return this.user.addRoles([role])
      }
      return false
   }

   rolesOnly(roles: string[]) {
      if (this.coreBasicService.console) console.log(this.coreBasicService.logPrefix, 'rolesOnly')

      return this.user.hasRoles(roles)
   }

   permissionsOnly(perms: string[]) {
      if (this.coreBasicService.console) console.log(this.coreBasicService.logPrefix, 'permissionsOnly')

      if (perms && perms.length) {
         for (let role of this.user.getRoles()) {
            if (this.isValidAppRole(role)) {
               for (let perm of perms) {
                  if (this.appRoles[role].includes(perm)) {
                     return true
                  }
               }
            }
         }
      }

      return false
   }

   getToken(): Promise<string> {
      return this.keycloakAngular.getToken()
   }
   // Access

   hasLicence() {
      let roles = this.keycloakAngular.getUserRoles(true)
      return roles.includes('LICENCE_PMP_TOOL_ADMIN') || roles.includes('LICENCE_PMP_TOOL_BASE')
   }

   isLicenceAdmin() {
      let roles = this.keycloakAngular.getUserRoles(true)
      return roles.includes('LICENCE_PMP_TOOL_ADMIN')
   }

   async isUserLogged() {
      return await this.keycloakAngular.isLoggedIn()
   }

   async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
      return new Promise(async (resolve, reject) => {
         console.log(this.coreBasicService.logPrefix, 'isAccessAllowed')
         if (!this.authenticated) {
            console.log(this.coreBasicService.logPrefix, 'no allowed')
            await this.keycloakAngular.login().catch((e) => console.error(e))
            reject(false)
         }
         resolve(true)
      })
   }

   private keycloakEventLog(type: KeycloakEventType) {
      if (this.coreBasicService.console) console.log(this.coreBasicService.logPrefix, 'Keycloak event - Type: ' + KeycloakEventType[type])
   }
}

